/**
 * Tests — attribute testing, review notes, sign-offs (firm-only).
 *   T6-B7  no templates → an inline "Create template" form; the template
 *          Select always offers "+ New template…".
 *   T6-B8  review notes come before the sign-off; the preparer responds, the
 *          author or a lead clears; reviewer sign-off waits for every note.
 *   T6-B9  a Fail cell offers "Draft exception" (control, test, wording prefilled).
 *   T6-H1  grid cells are never disabled while saving: writes queue, each
 *          cell shows its own "Saved" tick; focus stays on the cell.
 *   T6-H8  after a sign-off the grid is locked behind "Edit results (clears
 *          sign-offs)"; each cell can carry a note.
 *   T6-H13 compact status controls (Fail = danger + ✕, Not recorded = dashed),
 *          a captioned grid in a focusable scroll region, one card per item
 *          under 600px, the connector hint tied to its cell.
 *   T6-H14 preparer sign-off says how many cells are missing and marks them.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Card, EmptyState, Field, Input, Select, Textarea } from '@aigovops/design-system';
import { ActionDialog } from '../../components/today/shared';
import { firmApi, type FirmTemplate, type TemplateAttribute, type WorkpaperCell, type WorkpaperTest } from '../../lib/firm-api';
import { fmtDate, plural } from '../../lib/format';
import { stepUpUrlOf } from '../../lib/firm-step-up';
import { Chip, GuardedButton, StateBadge, TabHeading, errText, isLead, useAct, useDraft, useFirm, useFocusLater, useLoad, Loading } from './firm-kit';

function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const q = window.matchMedia(query);
    const u = () => setOn(q.matches);
    u();
    q.addEventListener?.('change', u);
    return () => q.removeEventListener?.('change', u);
  }, [query]);
  return on;
}

export function TestsTab() {
  const { slug, eng, o } = useFirm();
  const [params, setParams] = useSearchParams();
  const open = params.get('test');
  const l = useLoad(() => firmApi.tests(slug, eng), [slug, eng]);
  const reviewer = o.me.firm_role !== 'staff';
  const q = useLoad(() => firmApi.reviewQueue(slug, eng), [slug, eng]);
  const focus = useFocusLater();
  const openTest = (id: string) => setParams((p) => { p.set('test', id); return p; });
  if (open) {
    return <TestDetail id={open} onBack={() => { setParams((p) => { p.delete('test'); return p; }); focus(`test-${open}`); }} onChanged={() => { l.reload(); q.reload(); }} />;
  }
  return (
    <div className="gp-firm-stack">
      <TabHeading id="tests-h">Tests</TabHeading>
      {reviewer ? (
        <Loading l={q}>{(d) => d.awaiting_review.length + d.notes_to_clear.length === 0 ? null : (
          <section className="gp-firm-queue" aria-labelledby="queue-h">
            <h3 id="queue-h" className="gp-firm-h3">Your review queue</h3>
            <ul className="gp-firm-list">
              {d.awaiting_review.map((a) => (
                <li key={a.test_id}><a className="gp-firm-rowlink" href={`?tab=tests&test=${a.test_id}`} onClick={(e) => { e.preventDefault(); openTest(a.test_id); }}>{a.control_ref} · {a.title}</a> <span className="gp-firm-muted">prepared by {a.prepared_by_name}</span></li>
              ))}
              {d.notes_to_clear.map((n) => (
                <li key={n.note_id}><a className="gp-firm-rowlink" href={`?tab=tests&test=${n.test_id}`} onClick={(e) => { e.preventDefault(); openTest(n.test_id); }}>Note to clear: {n.body}</a></li>
              ))}
            </ul>
          </section>
        )}</Loading>
      ) : null}
      <Loading l={l}>{(d) => d.tests.length === 0
        ? <EmptyState title="No tests yet" action={<a href="#t-control">Start the first test</a>} />
        : (
          <ul className="gp-firm-list" aria-labelledby="tests-h">
            {d.tests.map((t) => (
              <li key={t.test_id} className="gp-firm-row gp-firm-row--link">
                <h3 className="gp-firm-row-title">
                  <a id={`test-${t.test_id}`} className="gp-firm-rowlink" href={`?tab=tests&test=${t.test_id}`} onClick={(e) => { e.preventDefault(); openTest(t.test_id); }}>{t.control_ref} · {t.title}</a>
                </h3>
                <div className="gp-firm-actions">
                  <StateBadge state={t.status} />
                  {t.open_notes ? <Chip tone="accent">{plural(t.open_notes, 'note')} open</Chip> : null}
                </div>
              </li>
            ))}
          </ul>
        )}</Loading>
      <NewTest onCreated={(id) => { l.reload(); openTest(id); }} />
    </div>
  );
}

function NewTest({ onCreated }: { onCreated: (id: string) => void }) {
  const { slug, eng, o, writable, lockId } = useFirm();
  const tpl = useLoad(() => firmApi.templates(slug, eng), [slug, eng]);
  const pops = useLoad(() => firmApi.populations(slug, eng), [slug, eng]);
  const act = useAct(() => undefined);
  const [control, setControl] = useDraft('t-control', '');
  const [template, setTemplate] = useDraft('t-template', '');
  const [sample, setSample] = useDraft('t-sample', '');
  const [tried, setTried] = useState(false);
  const samples = (pops.data?.populations ?? []).flatMap((p) => p.samples.map((s) => ({ id: s.lineage_id, label: `${p.kind.replace(/_/g, ' ')} · ${s.kind} · ${plural(s.sample_size, 'item')}` })));
  const templates = (tpl.data?.templates ?? []).filter((t: FirmTemplate) => !t.retired_at);
  const creating = template === '__new' || (tpl.data !== null && templates.length === 0);
  if (!writable) return <p className="gp-firm-muted" aria-describedby={lockId ?? undefined}>New tests are closed.</p>;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!control.trim() || !template || template === '__new' || !sample) return;
    let id = '';
    const ok = await act.run(async () => { id = (await firmApi.createTest(slug, eng, { control_ref: control.trim(), template_id: template, sample_id: sample })).test.test_id; }, `Test started for ${control.trim()}`);
    if (ok) { setControl(''); setTemplate(''); setSample(''); setTried(false); onCreated(id); }
  };
  return (
    <Card title="New test">
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {creating ? (
        isLead(o.me.firm_role)
          ? <TemplateForm onCreated={(t) => { tpl.reload(); setTemplate(t.template_id); }} onCancel={templates.length ? () => setTemplate('') : undefined} />
          : <EmptyState title="Your firm has no test templates yet" description="A partner or manager adds templates for the whole firm." />
      ) : null}
      <form className="gp-firm-form" onSubmit={submit} noValidate>
        <Field id="t-control" label="Control" error={tried && !control.trim() ? 'Name the control under test.' : null}>
          <Input value={control} onChange={(e) => setControl(e.target.value)} placeholder="CC6.2" />
        </Field>
        <Field id="t-template" label="Firm template" error={tried && (!template || template === '__new') ? 'Choose a template.' : null}>
          <Select value={template === '__new' ? '__new' : template} onChange={(e) => setTemplate(e.target.value)}>
            <option value="">Choose…</option>
            {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.title}</option>)}
            {isLead(o.me.firm_role) ? <option value="__new">+ New template…</option> : null}
          </Select>
        </Field>
        <Field id="t-sample" label="Sample" hint={samples.length ? undefined : 'Draw a sample under Populations first.'} error={tried && !sample ? 'Choose a sample.' : null}>
          <Select value={sample} onChange={(e) => setSample(e.target.value)}>
            <option value="">Choose…</option>
            {samples.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </Select>
        </Field>
        <div className="gp-firm-actions">
          <GuardedButton type="submit" busy={act.busy}>Start test</GuardedButton>
          {!samples.length ? <a href="?tab=populations">Draw a sample</a> : null}
        </div>
      </form>
    </Card>
  );
}

/** T6-B7: a firm template — a title plus repeatable attributes. */
export function TemplateForm({ onCreated, onCancel }: { onCreated: (t: FirmTemplate) => void; onCancel?: () => void }) {
  const { slug, eng } = useFirm();
  const act = useAct(() => undefined);
  const [title, setTitle] = useDraft('tpl-title', '');
  const [attrs, setAttrs] = useDraft<string[]>('tpl-attrs', ['']);
  const [tried, setTried] = useState(false);
  const focus = useFocusLater();
  const labels = attrs.map((a) => a.trim()).filter(Boolean);
  const errs = { title: title.trim().length < 3 ? 'Name the template (at least 3 characters).' : null, attrs: labels.length === 0 || labels.some((a) => a.length < 2) ? 'Add at least one attribute (2+ characters each).' : null };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (errs.title || errs.attrs) return;
    const used = new Set<string>();
    const attributes: TemplateAttribute[] = labels.map((label, i) => {
      let key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || `attr_${i + 1}`;
      if (!/^[a-z]/.test(key)) key = `a_${key}`;
      while (used.has(key)) key = `${key}_${i + 1}`;
      used.add(key);
      return { key, label };
    });
    let made: FirmTemplate | null = null;
    if (await act.run(async () => { made = (await firmApi.createTemplate(slug, eng, { title: title.trim(), attributes })).template; }, `Template “${title.trim()}” created`)) {
      setTitle(''); setAttrs(['']); setTried(false);
      if (made) onCreated(made);
    }
  };
  return (
    <form className="gp-firm-form gp-firm-subform" onSubmit={submit} noValidate aria-labelledby="tpl-h">
      <h3 id="tpl-h" className="gp-firm-h3">Create template</h3>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Field id="tpl-title" label="Template name" error={tried ? errs.title : null}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Termination testing" />
      </Field>
      <fieldset className="gp-firm-fieldset">
        <legend>Attributes to test</legend>
        {attrs.map((a, i) => (
          <div key={i} className="gp-firm-form--row">
            <Field id={`tpl-attr-${i}`} label={`Attribute ${i + 1}`}>
              <Input value={a} onChange={(e) => setAttrs(attrs.map((x, j) => (j === i ? e.target.value : x)))} />
            </Field>
            {attrs.length > 1 ? <GuardedButton compact variant="ghost" aria-label={`Remove attribute ${i + 1}`} onClick={() => setAttrs(attrs.filter((_, j) => j !== i))}>Remove</GuardedButton> : null}
          </div>
        ))}
        {tried && errs.attrs ? <p className="gp-firm-err" role="alert">{errs.attrs}</p> : null}
        <GuardedButton compact onClick={() => { setAttrs([...attrs, '']); focus(`tpl-attr-${attrs.length}`); }}>Add attribute</GuardedButton>
      </fieldset>
      <div className="gp-firm-actions">
        <GuardedButton type="submit" busy={act.busy}>Create template</GuardedButton>
        {onCancel ? <GuardedButton variant="ghost" onClick={onCancel}>Cancel</GuardedButton> : null}
      </div>
    </form>
  );
}

const RESULT_LABEL: Record<string, string> = { '': 'Not recorded', pass: '✓ Pass', fail: '✕ Fail', na: 'N/A' };
type CellState = 'saving' | 'saved' | 'error';

function TestDetail({ id, onBack, onChanged }: { id: string; onBack: () => void; onChanged: () => void }) {
  const { slug, eng, o, writable, lockId, requireStepUp, announce } = useFirm();
  const [, setParams] = useSearchParams();
  const l = useLoad(() => firmApi.test(slug, eng, id), [slug, eng, id]);
  const [t, setT] = useState<WorkpaperTest | null>(null);
  useEffect(() => { if (l.data) setT(l.data.test); }, [l.data]);
  const act = useAct(() => { l.reload(); onChanged(); });
  const focus = useFocusLater();
  const narrow = useMedia('(max-width: 599px)');
  const phase = o.phase;
  const canTest = writable; // results: fieldwork only
  const canSign = phase === 'fieldwork' || phase === 'assembly'; // sign-offs + notes allowed in assembly (allowAssembly)
  const [unlocked, setUnlocked] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [cellState, setCellState] = useState<Record<string, CellState>>({});
  const [cellError, setCellError] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<WorkpaperCell | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const [showMissing, setShowMissing] = useState(false);

  useEffect(() => { focus('test-detail-h'); }, [focus]);

  /** T6-H1: queue the write, keep the control enabled, tick the cell when saved. */
  const record = useCallback((cell: WorkpaperCell, result: string, note?: string | null) => {
    if (!result) return;
    const key = `${cell.item_key}|${cell.attribute_key}`;
    setCellState((s) => ({ ...s, [key]: 'saving' }));
    setT((cur) => (cur ? { ...cur, cells: cur.cells.map((c) => (c.item_key === cell.item_key && c.attribute_key === cell.attribute_key ? { ...c, result: result as WorkpaperCell['result'], note: note === undefined ? c.note : note } : c)) } : cur));
    queue.current = queue.current.then(async () => {
      try {
        const r = await firmApi.result(slug, eng, id, { item_key: cell.item_key, attribute_key: cell.attribute_key, result, note: note === undefined ? cell.note : note });
        setT(r.test);
        setCellState((s) => ({ ...s, [key]: 'saved' }));
        setCellError(null);
        onChanged();
      } catch (e) {
        setCellState((s) => ({ ...s, [key]: 'error' }));
        const url = stepUpUrlOf(e);
        if (url) requireStepUp(url, { method: 'result', args: [slug, eng, id, { item_key: cell.item_key, attribute_key: cell.attribute_key, result }], message: 'Result saved' });
        else setCellError(errText(e));
      }
    });
  }, [slug, eng, id, onChanged, requireStepUp]);

  if (!t) return <div className="gp-firm-stack"><GuardedButton compact variant="ghost" onClick={onBack}>← All tests</GuardedButton><Loading l={l}>{() => null}</Loading></div>;

  const signed = t.signoffs.length > 0;
  const locked = signed && !unlocked;
  const editable = canTest && !locked;
  const missing = t.cells.filter((c) => c.result === null);
  const preparer = t.signoffs.find((s) => s.kind === 'preparer');
  const isPreparer = preparer?.user_id === o.me.user_id;
  const uncleared = t.review_notes.filter((n) => n.status !== 'cleared');
  const reviewer = o.me.firm_role !== 'staff';
  const cellOf = (item: string, a: string) => t.cells.find((x) => x.item_key === item && x.attribute_key === a)!;
  const failDraft = (c: WorkpaperCell, a: TemplateAttribute) => {
    setParams((p) => {
      p.set('tab', 'exceptions');
      p.delete('test');
      p.set('draft_control', t.control_ref);
      p.set('draft_test', t.test_id);
      p.set('draft_wording', `For ${c.item_key}, “${a.label}” was not met${c.note ? ` (${c.note})` : ''}.`);
      return p;
    });
  };

  const cellControl = (item: string, a: TemplateAttribute, idPrefix: string) => {
    const c = cellOf(item, a.key);
    const key = `${item}|${a.key}`;
    const st = cellState[key];
    const hintId = c.hint ? `${idPrefix}-hint` : undefined;
    const flagged = showMissing && c.result === null;
    return (
      <div className={`gp-firm-cell${flagged ? ' gp-firm-cell--missing' : ''}`}>
        <select
          id={idPrefix}
          className="gp-firm-result"
          data-result={c.result ?? 'none'}
          aria-label={`${item} · ${a.label}`}
          aria-describedby={[hintId, !editable && lockId ? lockId : null, !editable && locked ? `${t.test_id}-lock` : null].filter(Boolean).join(' ') || undefined}
          aria-disabled={!editable || undefined}
          value={c.result ?? ''}
          onChange={(e) => { if (editable) record(c, e.target.value); }}
        >
          {(['', 'pass', 'fail', 'na'] as const).map((v) => <option key={v} value={v} disabled={v === '' && c.result !== null}>{RESULT_LABEL[v]}</option>)}
        </select>
        {st === 'saving' ? <span className="gp-firm-tick" aria-hidden="true">…</span> : st === 'saved' ? <span className="gp-firm-tick gp-firm-ok">✓ Saved</span> : st === 'error' ? <span className="gp-firm-tick gp-firm-lock">Not saved</span> : null}
        {c.hint ? <span className="gp-firm-hint" id={hintId}>Connector: {c.hint}</span> : null}
        {c.note ? <span className="gp-firm-hint">Note: {c.note}</span> : null}
        <span className="gp-firm-cell-actions">
          {editable ? <button type="button" className="gp-firm-linkbtn" aria-label={`Note for ${item} · ${a.label}`} onClick={() => { setNoteFor(c); focus('cell-note'); }}>Note</button> : null}
          {c.result === 'fail' && writable ? <button type="button" className="gp-firm-linkbtn" onClick={() => failDraft(c, a)} aria-label={`Draft exception for ${item} · ${a.label}`}>Draft exception</button> : null}
        </span>
      </div>
    );
  };

  return (
    <div className="gp-firm-stack">
      <div className="gp-firm-actions"><GuardedButton compact variant="ghost" onClick={onBack}>← All tests</GuardedButton></div>
      <TabHeading id="test-detail-h" right={<StateBadge state={t.status} />}>{t.control_ref} · {t.title}</TabHeading>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {cellError ? <Alert variant="error">{cellError}</Alert> : null}
      {locked && canTest ? (
        <div className="gp-firm-actions">
          <span className="gp-firm-muted" id={`${t.test_id}-lock`}>Signed off · results are locked</span>
          <GuardedButton compact onClick={() => setConfirmEdit(true)}>Edit results (clears sign-offs)</GuardedButton>
        </div>
      ) : null}
      {narrow ? (
        <ul className="gp-firm-list" aria-label="Results by item">
          {t.item_keys.map((item) => (
            <li key={item} className="gp-firm-row">
              <h3 className="gp-firm-row-title">{item}</h3>
              {t.attributes.map((a) => (
                <div key={a.key} className="gp-firm-cardcell">
                  <span className="gp-firm-muted">{a.label}</span>
                  {cellControl(item, a, `c-${item}-${a.key}`)}
                </div>
              ))}
            </li>
          ))}
        </ul>
      ) : (
        <div className="gp-firm-grid-wrap" role="region" aria-labelledby={`${t.test_id}-cap`} tabIndex={0}>
          <table className="gp-firm-grid">
            <caption id={`${t.test_id}-cap`}>Results for {plural(t.item_keys.length, 'item')} × {plural(t.attributes.length, 'attribute')}</caption>
            <thead><tr><th scope="col">Item</th>{t.attributes.map((a) => <th key={a.key} scope="col">{a.label}</th>)}</tr></thead>
            <tbody>
              {t.item_keys.map((item) => (
                <tr key={item}>
                  <th scope="row" className="gp-firm-nowrap">{item}</th>
                  {t.attributes.map((a) => <td key={a.key}>{cellControl(item, a, `c-${item}-${a.key}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {noteFor ? <CellNote cell={noteFor} label={t.attributes.find((a) => a.key === noteFor.attribute_key)?.label ?? ''} onSave={(n) => { if (noteFor.result) record(noteFor, noteFor.result, n); setNoteFor(null); }} onCancel={() => setNoteFor(null)} /> : null}

      <ReviewNotes test={t} reviewer={reviewer} canWrite={canSign} onChanged={() => { l.reload(); onChanged(); }} />

      <section className="gp-firm-signoff" aria-labelledby="signoff-h">
        <h3 id="signoff-h" className="gp-firm-h3">Sign-off</h3>
        <p className="gp-firm-muted">
          {t.signoffs.length ? t.signoffs.map((s) => `${s.kind === 'preparer' ? 'Prepared' : 'Reviewed'} by ${s.display_name} ${fmtDate(s.signed_at)}`).join(' · ') : 'Not signed off yet'}
        </p>
        <div className="gp-firm-actions">
          {t.status === 'in_progress' && canSign ? (
            <GuardedButton variant="primary" busy={act.busy} blocked={missing.length > 0}
              reason={missing.length ? <>{plural(missing.length, 'cell')} not recorded · <button type="button" className="gp-firm-linkbtn" onClick={() => { setShowMissing(true); const m = missing[0]!; focus(`c-${m.item_key}-${m.attribute_key}`); }}>Show</button></> : null}
              onClick={() => void act.call('signoff', [slug, eng, t.test_id, 'preparer'], 'Signed off as preparer')}>Sign off as preparer</GuardedButton>
          ) : null}
          {t.status === 'prepared' && reviewer && !isPreparer && canSign ? (
            <GuardedButton variant="primary" busy={act.busy} blocked={uncleared.length > 0} reason={uncleared.length ? `${plural(uncleared.length, 'review note')} not cleared` : null}
              onClick={() => void act.call('signoff', [slug, eng, t.test_id, 'reviewer'], 'Signed off as reviewer')}>Sign off as reviewer</GuardedButton>
          ) : null}
          {t.status === 'prepared' && !reviewer ? <span className="gp-firm-why">A senior, manager or partner reviews.</span> : null}
        </div>
      </section>

      <ActionDialog open={confirmEdit} title="Edit results?" submitLabel="Edit results" onClose={() => setConfirmEdit(false)}
        onSubmit={() => { setUnlocked(true); setConfirmEdit(false); announce('Results unlocked · saving a change clears the sign-offs'); }}>
        <p>Changing any result clears the preparer and reviewer sign-offs on this test. They sign off again afterwards.</p>
      </ActionDialog>
    </div>
  );
}

function CellNote({ cell, label, onSave, onCancel }: { cell: WorkpaperCell; label: string; onSave: (note: string | null) => void; onCancel: () => void }) {
  const [v, setV] = useState(cell.note ?? '');
  return (
    <form className="gp-firm-form gp-firm-subform" onSubmit={(e) => { e.preventDefault(); onSave(v.trim() || null); }} noValidate>
      <Field id="cell-note" label={`Note for ${cell.item_key} · ${label}`} hint={cell.result ? undefined : 'Record a result first; the note saves with it.'}>
        <Input value={v} onChange={(e) => setV(e.target.value)} />
      </Field>
      <div className="gp-firm-actions">
        <GuardedButton type="submit" compact blocked={!cell.result}>Save note</GuardedButton>
        <GuardedButton compact variant="ghost" onClick={onCancel}>Cancel</GuardedButton>
      </div>
    </form>
  );
}

function ReviewNotes({ test: t, reviewer, canWrite, onChanged }: { test: WorkpaperTest; reviewer: boolean; canWrite: boolean; onChanged: () => void }) {
  const { slug, eng, o } = useFirm();
  const act = useAct(onChanged);
  const [body, setBody] = useDraft(`note-new-${t.test_id}`, '');
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [reply, setReply] = useDraft(`note-reply-${t.test_id}`, '');
  const [tried, setTried] = useState(false);
  const focus = useFocusLater();
  const lead = isLead(o.me.firm_role);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (body.trim().length < 3) return;
    if (await act.call('addNote', [slug, eng, t.test_id, body.trim()], 'Review note added')) { setBody(''); setTried(false); }
  };
  const respond = async (e: FormEvent, noteId: string) => {
    e.preventDefault();
    setTried(true);
    if (reply.trim().length < 2) return;
    if (await act.call('addressNote', [slug, eng, noteId, reply.trim()], 'Response sent · note addressed')) { setReply(''); setReplyFor(null); setTried(false); focus(`note-${noteId}`); }
  };
  return (
    <section aria-labelledby="notes-h" className="gp-firm-notes">
      <h3 id="notes-h" className="gp-firm-h3">Review notes</h3>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {t.review_notes.length === 0 ? <p className="gp-firm-muted">No review notes.</p> : (
        <ul className="gp-firm-list">
          {t.review_notes.map((n) => (
            <li key={n.note_id} className="gp-firm-note" id={`note-${n.note_id}`} tabIndex={-1}>
              <div className="gp-firm-row-head">
                <span><strong>{n.author_name}</strong>: {n.body}</span>
                <StateBadge state={n.status} />
              </div>
              {n.response ? <p className="gp-firm-muted">Response from {n.addressed_by_name ?? 'the preparer'}: {n.response}</p> : null}
              {canWrite ? (
                <div className="gp-firm-actions">
                  {n.status === 'open' && n.author_user_id !== o.me.user_id ? (
                    replyFor === n.note_id ? (
                      <form className="gp-firm-form gp-firm-form--row" onSubmit={(e) => void respond(e, n.note_id)} noValidate>
                        <Field id={`reply-${n.note_id}`} label="Your response" error={tried && reply.trim().length < 2 ? 'Write a response.' : null}>
                          <Input value={reply} onChange={(e) => setReply(e.target.value)} />
                        </Field>
                        <div className="gp-firm-actions">
                          <GuardedButton type="submit" compact busy={act.busy}>Respond and mark addressed</GuardedButton>
                          <GuardedButton compact variant="ghost" onClick={() => setReplyFor(null)}>Cancel</GuardedButton>
                        </div>
                      </form>
                    ) : <GuardedButton compact onClick={() => { setReplyFor(n.note_id); focus(`reply-${n.note_id}`); }}>Respond</GuardedButton>
                  ) : null}
                  {n.status !== 'cleared' && (n.author_user_id === o.me.user_id || lead) ? (
                    <GuardedButton compact busy={act.busy} onClick={() => void act.call('clearNote', [slug, eng, n.note_id], 'Review note cleared')}>Clear</GuardedButton>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {reviewer && canWrite ? (
        <form className="gp-firm-form" onSubmit={add} noValidate>
          <Field id={`note-new-${t.test_id}`} label="Review note" hint="Only your firm sees review notes." error={tried && body.trim().length < 3 ? 'Write the note (at least 3 characters).' : null}>
            <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="gp-firm-actions"><GuardedButton type="submit" busy={act.busy}>Add note</GuardedButton></div>
        </form>
      ) : null}
    </section>
  );
}
